import React, { useCallback, useState } from 'react';
import { ChevronLeft, ChevronRight, PanelLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Square, Diamond, PlayCircle, StopCircle, StickyNote } from 'lucide-react';
import { SymbolPalette } from '../ShapePanel/SymbolPalette';
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../../services/api/uiButtonStatsService';
import { BlockType } from '../../lib/types';

interface ShapeLibrarySidebarProps {
  isEditing: boolean;
}

const SIDEBAR_WIDTH = 240;
const COLLAPSED_WIDTH = 40;

/**
 * Draw.io-style left shape library. Only visible while editing.
 * Collapse control is wired — no decorative-only chrome.
 */
export function ShapeLibrarySidebar({ isEditing }: ShapeLibrarySidebarProps) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  const onDragStart = useCallback(({ blockType }: { blockType: BlockType; bpmnSymbol?: string; dmnSymbol?: string; vsmSymbol?: string }) => {
    const trackByKind: Record<string, string> = {
      process: UI_BUTTON_EVENTS.mapping.shapeDragProcess,
      decision: UI_BUTTON_EVENTS.mapping.shapeDragDecision,
      start: UI_BUTTON_EVENTS.mapping.shapeDragStart,
      end: UI_BUTTON_EVENTS.mapping.shapeDragEnd,
      note: UI_BUTTON_EVENTS.mapping.shapeDragNote,
    };
    const key = trackByKind[blockType] ?? trackByKind.process;
    void trackUiButtonEventAsync(key);
  }, []);

  if (!isEditing) return null;

  return (
    <aside
      className="shrink-0 flex flex-col border-r border-gray-200 bg-[#f7f7f8] h-full z-20"
      style={{
        width: collapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        transition: 'width 180ms ease',
      }}
      data-tutorial="flowchart-edit"
      aria-label={t('features.swimlane.shapes')}
    >
      <div className="flex items-center justify-between gap-1 px-2 h-9 border-b border-gray-200 shrink-0 bg-white/70">
        {!collapsed && (
          <div className="flex items-center gap-1.5 min-w-0">
            <PanelLeft className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <span className="text-xs font-semibold text-gray-700 truncate">
              {t('features.swimlane.shapes')}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="p-1 rounded hover:bg-gray-200 text-gray-600 transition-colors ml-auto"
          title={collapsed ? t('features.swimlane.shapes') : undefined}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {!collapsed && (
        <SymbolPalette
          variant="library"
          className="flex-1 min-h-0"
          epcShapes={[
            { type: 'process', label: t('words.proces'), color: 'blue', Icon: Square },
            { type: 'decision', label: t('words.decision'), color: 'purple', Icon: Diamond },
            { type: 'start', label: t('words.start'), color: 'green', Icon: PlayCircle },
            { type: 'note', label: String(t('words.note')), color: 'yellow', Icon: StickyNote },
            { type: 'end', label: t('words.end'), color: 'red', Icon: StopCircle },
          ]}
          onDragStart={onDragStart}
        />
      )}
    </aside>
  );
}

export default ShapeLibrarySidebar;
